import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from 'hooks'
import { useFetch } from 'hooks/useFetch'
import { useIntl } from 'react-intl'
import { messages } from '../../messages'
import { CouponForm } from '../Editor'

type UseCouponsParams = CouponForm

export const toApi = (config: UseCouponsParams) => {
  if (config.group.checked) {
    return {
      url: '/api/proxy/rnb/v2/pvt/coupon/group',
      params: [
        {
          quantity: config.group.value,
          couponConfiguration: {
            couponCode: config.name,
            expirationIntervalPerUse: '00:00:00',
            maxItemsPerClient: 0,
            maxUsage: config.limit.checked ? config.limit.value : null,
            utmCampaign: config.utmCampaign,
            utmSource: config.utmSource,
          },
        },
      ],
    }
  }
  return {
    url: '/api/proxy/rnb/v1/pvt/coupon',
    params: {
      couponCode: config.name,
      expirationIntervalPerUse: '00:00:00',
      maxItemsPerClient: 0,
      maxUsage: config.limit.checked ? config.limit.value : null,
      utmCampaign: config.utmCampaign,
      utmSource: config.utmSource,
      isArchived: false,
    },
  }
}

export function useCouponsMutation() {
  const { fetcherWrapper } = useFetch()
  const queryClient = useQueryClient()
  const { errorToast, positiveToast } = useToast()
  const { $t } = useIntl()

  const mutation = (config: UseCouponsParams) => {
    const { params, url } = toApi(config)
    const urlObject = new URL(url, window.location.origin)

    return fetcherWrapper(urlObject, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    })
  }

  const { mutate: save, isPending } = useMutation({
    mutationFn: mutation,
    onSuccess: async (response, config) => {
      const data = await response.json()
      queryClient.setQueryData(['coupon-created'], data)
      queryClient.refetchQueries({
        queryKey: ['coupons-by-utm'],
      })
      positiveToast($t(messages.toast.success, { group: config.group.checked }))
    },
    onError: (_, config) =>
      errorToast($t(messages.toast.error, { group: config.group.checked })),
  })

  return {
    save,
    isLoading: isPending,
  }
}
